import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { trackActivityEvent } from "./analytics/trackActivityEvent";

function extractHttpStatus(error) {
  return (
    error?.response?.status ??
    error?.status ??
    error?.cause?.response?.status ??
    error?.cause?.status
  );
}

function reportApiHttpError(error, source) {
  const status = extractHttpStatus(error);
  const msg = String(error?.message || "").slice(0, 280);
  if (status === 404) {
    trackActivityEvent("api_error_404", { source, message: msg });
  } else if (typeof status === "number" && status >= 500) {
    trackActivityEvent("api_error_500", { source, status, message: msg });
  }
}

export const _queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes default stale time
      refetchOnMount: false, // Don't refetch on mount if data is fresh
      refetchOnWindowFocus: false, // Don't refetch when window gains focus
      refetchOnReconnect: false, // Don't refetch on reconnect
      retry: 1, // Only retry once on failure
      retryDelay: 1000, // Wait 1 second before retry
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      console.error(error);
      reportApiHttpError(error, "react_query_query");
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      console.error(error);
      reportApiHttpError(error, "react_query_mutation");
    },
  }),
});

// This is pretty much the boilerplate for tanstack optimistic updates, collected here for convienence
// https://tanstack.com/query/v4/docs/framework/react/guides/optimistic-updates
export const optimisticUpdate = async (queryClient, queryKey, mutationFn) => {
  await queryClient.cancelQueries(queryKey);
  const previousData = queryClient.getQueryData(queryKey);
  queryClient.setQueryData(queryKey, (old) => {
    return mutationFn(old);
  });
  return previousData;
};
